// Client-side relevance ranking for the salon marketplace.
//
// Why client-side: the backend list endpoint orders via a Prisma `orderBy` on
// plain columns, which can't express a weighted composite — and the strongest
// signal (distance) is per-user and only known in the browser. At cold-start
// (one city, low inventory) the whole filtered set fits on the client, so we
// fetch it with a high limit and rank here. When inventory grows past a single
// page, the non-distance part of this score moves to a stored column + a job;
// the distance term stays client-side.
//
// The `isFeatured` (paid-subscription) boost is intentionally OMITTED for now:
// every salon is on a free trial, so there is nothing to promote yet, and a
// promoted result also needs a visible "Promovat" label for transparency. When
// B2B subscriptions go live, multiply/add to the score in `relevanceScore`
// (e.g. `+ (salon.isFeatured ? 15 : 0)`) and render the label — the ranking
// system around it is already production-validated by then.
//
// Score is 0–100, higher = more relevant:
//   60  Bayesian rating  — shrinks a thin review count toward the platform mean
//   15  Profile completeness — cover photo + services + description
//   15  Proximity         — only with a geolocation fix; 0 at >= 10 km
//   10  Freshness         — decays linearly over 30 days from createdAt

// M — the prior the Bayesian average pulls toward. Grooming/beauty ratings skew
// high, so a brand-new salon with 0 reviews is treated as "average" (mid-list),
// never dead last.
const BAYES_PRIOR_MEAN = 4.5;
// C — how many real reviews a salon needs before its own average outweighs the
// prior. Low (3) because at launch even a handful of reviews is signal.
const BAYES_CONFIDENCE = 3;

const FRESHNESS_WINDOW_DAYS = 30; // freshness boost fully decays after this
const PROXIMITY_ZERO_KM = 10;     // proximity points reach 0 at this distance

// Great-circle distance (km) between two lat/lng points.
export function haversineKm(aLat, aLng, bLat, bLng) {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// Bayesian-adjusted rating in the 0–5 range. Worked example (C=3, M=4.5):
//   0 reviews            → 4.50 (protected: mid-list, not buried)
//   1 review  of 5.0★    → 4.62
//   50 reviews of 4.8★   → 4.78 (volume of good reviews wins, as it should)
export function bayesianRating(avgRating, reviewCount) {
  const n = Math.max(0, Number(reviewCount) || 0);
  const r = Number(avgRating) || 0;
  return (BAYES_CONFIDENCE * BAYES_PRIOR_MEAN + n * r) / (BAYES_CONFIDENCE + n);
}

// 0–15. Additive (not all-or-nothing) so partially-filled profiles still rank
// above empty ones and ties break sensibly.
function completenessPoints(salon) {
  let pts = 0;
  // Real cover/gallery imagery (not just a logo).
  if (salon.hasCover || (Array.isArray(salon.photos) && salon.photos.length > 0)) pts += 5;
  // The list caps attached services at 5, so serviceCount >= 5 reliably means
  // "has at least 5 services"; >= 2 gets partial credit.
  const services = Number(salon.serviceCount) || 0;
  if (services >= 5) pts += 5;
  else if (services >= 2) pts += 3;
  // A written description.
  if ((salon.description || '').trim().length >= 40) pts += 5;
  return pts;
}

// 0–15, only when we have the user's location. Full near, linear down to 0 at
// PROXIMITY_ZERO_KM. A great salon 7 km away still outranks a mediocre one next
// door — distance is a tie-shaper, not a filter.
function proximityPoints(salon, userPos) {
  if (!userPos || !salon.latitude || !salon.longitude) return 0;
  const km = haversineKm(userPos.lat, userPos.lng, salon.latitude, salon.longitude);
  const slope = 15 / PROXIMITY_ZERO_KM;
  return Math.max(0, 15 - km * slope);
}

// 0–10, decays linearly over FRESHNESS_WINDOW_DAYS so newly-listed salons get a
// temporary visibility bump while they accumulate their first reviews.
function freshnessPoints(salon) {
  if (!salon.createdAt) return 0;
  const parsedTime = new Date(salon.createdAt).getTime();
  // Corrupt / unparseable date → NO boost (0), NOT the max. The old
  // `!Number.isFinite(ageDays)` branch returned 10 for a NaN date, which would
  // wrongly rocket a salon with a bad createdAt to the top of the list.
  if (Number.isNaN(parsedTime)) return 0;
  const ageDays = (Date.now() - parsedTime) / 86_400_000;
  if (ageDays < 0) return 10; // future date (clock skew) → treat as brand-new
  return Math.max(0, 10 - ageDays * (10 / FRESHNESS_WINDOW_DAYS));
}

// Composite 0–100 relevance score. `userPos` optional ({lat,lng}); without it
// the proximity term is simply 0 and the rest still rank the list.
export function relevanceScore(salon, userPos) {
  const ratingValue = salon.ratingValue != null ? salon.ratingValue : Number(salon.rating) || 0;
  return (
    bayesianRating(ratingValue, salon.reviewCount) * 12 + // 0–5 → 0–60
    completenessPoints(salon) +
    proximityPoints(salon, userPos) +
    freshnessPoints(salon)
  );
}

function minPrice(salon) {
  const m = salon?.priceRange?.min;
  return m && m > 0 ? m : Infinity; // unpriced salons sink to the bottom
}

// Sort a salon list by the chosen UI mode. Pure (returns a new array).
//   'recomandate' (default) — composite relevance score
//   'distance'              — nearest first (falls back to relevance w/o a fix)
//   'price'                 — cheapest base service first
//   'newest'                — most recently listed first
export function sortSalons(salons, mode = 'recomandate', userPos = null) {
  const arr = [...(salons || [])];
  switch (mode) {
    case 'distance':
      if (!userPos) return sortSalons(arr, 'recomandate', userPos);
      arr.sort((a, b) => {
        const da = a.latitude && a.longitude ? haversineKm(userPos.lat, userPos.lng, a.latitude, a.longitude) : Infinity;
        const db = b.latitude && b.longitude ? haversineKm(userPos.lat, userPos.lng, b.latitude, b.longitude) : Infinity;
        return da - db;
      });
      return arr;
    case 'price':
      arr.sort((a, b) => minPrice(a) - minPrice(b));
      return arr;
    case 'newest':
      // Newest first. Parse to timestamps rather than comparing the ISO strings
      // lexicographically: a corrupt createdAt like "invalid-date" would sort
      // ABOVE real dates ("i" > "2") and wrongly top the list — the same
      // corrupt-date case freshnessPoints already guards against. Missing or
      // unparseable dates collapse to 0 (epoch) so they sort last. Lists are
      // < 100 items, so the Date-parse cost is negligible.
      arr.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
      return arr;
    default: {
      // Schwartzian transform — score each salon ONCE (relevanceScore runs a
      // haversine + date parse + string work), then sort by the precomputed
      // score, instead of recomputing it O(N log N) times inside the comparator.
      const scored = arr.map((salon) => ({ salon, score: relevanceScore(salon, userPos) }));
      scored.sort((a, b) => b.score - a.score);
      return scored.map((x) => x.salon);
    }
  }
}
